<template>
  <AdminShell
    :page-title="pageTitle"
    :page-subtitle="pageSubtitle"
    active-menu="promotion"
  >
    <template #actions>
      <el-button v-if="isPendingPromotionView" plain @click="goToNotifications">返回站内信</el-button>
      <template v-else>
        <el-button plain @click="goToTotalBills">推广总账单</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="goToCreate">新建推广</el-button>
      </template>
    </template>

    <section class="promotion-list-page">
      <article class="promotion-list-toolbar">
        <el-input
          v-model.trim="filters.keyword"
          clearable
          placeholder="搜索推广名称或目标"
          prefix-icon="el-icon-search"
          @keyup.enter.native="fetchCampaigns"
          @clear="fetchCampaigns"
        />
        <el-select
          v-if="!isPendingPromotionView"
          v-model="filters.status"
          clearable
          placeholder="全部状态"
          @change="fetchCampaigns"
        >
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-button icon="el-icon-refresh" :loading="loading" @click="fetchCampaigns">刷新</el-button>
      </article>

      <article class="promotion-list-card">
        <el-table
          v-loading="loading"
          :data="campaigns"
          row-key="id"
          empty-text="暂无推广活动"
        >
          <el-table-column label="推广活动" min-width="220">
            <template slot-scope="{ row }">
              <div class="campaign-cell">
                <strong>{{ row.name || row.campaign_name || '未命名推广' }}</strong>
                <span>{{ objectiveLabel(row.objective) }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column v-if="isPendingPromotionView" label="商户 ID" width="130">
            <template slot-scope="{ row }">
              <span>{{ row.commercial_id || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="预算" min-width="160">
            <template slot-scope="{ row }">
              <span>{{ budgetText(row) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="状态" width="120">
            <template slot-scope="{ row }">
              <span class="status-chip" :class="`is-${row.status}`">{{ statusLabel(row.status) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="素材数" width="100">
            <template slot-scope="{ row }">
              <span>{{ row.active_ads_count || 0 }}</span>
            </template>
          </el-table-column>

          <el-table-column label="最近账单" min-width="260">
            <template slot-scope="{ row }">
              <div class="bill-cell">
                <strong>{{ latestBillText(row) }}</strong>
                <span>{{ latestBillMeta(row) }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="更新时间" width="170">
            <template slot-scope="{ row }">
              <span>{{ formatDateTime(row.updated_at) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="280" fixed="right">
            <template slot-scope="{ row }">
              <div class="action-group">
                <el-button type="text" @click="editCampaign(row)">
                  {{ isPendingPromotionView ? '查看详情' : '编辑' }}
                </el-button>
                <el-button v-if="!isPendingPromotionView" type="text" @click="openBills(row)">账单明细</el-button>
                <el-button
                  v-if="!isPendingPromotionView && canContinuePay(row)"
                  type="text"
                  @click="continuePay(row)"
                >
                  继续支付
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </article>
    </section>
  </AdminShell>
</template>

<script>
import { getUserInfo, listPromotionCampaigns } from '@/api'
import AdminShell from '@/components/admin/AdminShell.vue'
import { promotionPaymentStatusLabel } from '@/constants/promotion-payment'
import {
  STATUS_OPTIONS,
  formatDateTime,
  formatPromotionBudgetText,
  isPayableStatus,
  promotionObjectiveLabel,
  promotionStatusLabel,
  resolveCommercialId,
} from '@/utils/promotion-form'

export default {
  name: 'PromotionListPage',
  components: {
    AdminShell,
  },
  data() {
    return {
      loading: false,
      commercialId: '',
      campaigns: [],
      filters: {
        keyword: '',
        status: '',
        limit: 50,
        offset: 0,
      },
      statusOptions: STATUS_OPTIONS,
    }
  },
  async mounted() {
    await this.initialize()
  },
  computed: {
    isPendingPromotionView() {
      return String(this.commercialId) === '1' && this.$route.query.view === 'pending-promotions'
    },
    pageTitle() {
      return this.isPendingPromotionView ? '待处理推广' : '推广管理'
    },
    pageSubtitle() {
      return this.isPendingPromotionView
        ? '查看客户已启用的推广，快速定位站内信通知关联的推广来源。'
        : '按推广查看预算、状态和最近账单，统一从列表进入编辑与支付流程。'
    },
  },
  watch: {
    '$route.fullPath': {
      async handler() {
        if (!this.commercialId) return
        await this.fetchCampaigns()
      },
    },
  },
  methods: {
    async initialize() {
      await this.loadCommercialId()
      await this.fetchCampaigns()
      this.handleRouteFeedback()
    },
    async loadCommercialId() {
      const { data } = await getUserInfo()
      this.commercialId = String(resolveCommercialId(data) || '')
    },
    async fetchCampaigns() {
      if (!this.commercialId) return
      this.loading = true
      try {
        const params = {
          commercial_id: this.commercialId,
          keyword: this.filters.keyword || undefined,
          limit: this.filters.limit,
          offset: this.filters.offset,
        }
        if (!this.isPendingPromotionView) {
          params.status = this.filters.status || undefined
        }
        const res = await listPromotionCampaigns(params)
        this.campaigns = Array.isArray(res?.data?.items) ? res.data.items : []
      } catch (error) {
        this.campaigns = []
        this.$message.error(error.message || '加载推广列表失败')
      } finally {
        this.loading = false
      }
    },
    handleRouteFeedback() {
      const query = { ...(this.$route.query || {}) }
      let changed = false

      if (query.paymentStatus === 'paid') {
        this.$message.success('支付成功，推广已更新')
        delete query.paymentStatus
        changed = true
      }
      if (query.saved === '1') {
        this.$message.success(query.draftSaved === '1' ? '草稿保存成功' : '推广保存成功')
        delete query.saved
        delete query.draftSaved
        changed = true
      }

      if (changed) {
        this.$router.replace({ path: this.$route.path, query }).catch(() => {})
      }
    },
    goToCreate() {
      this.$router.push('/promotion/create')
    },
    goToTotalBills() {
      this.$router.push('/promotion/bills')
    },
    goToNotifications() {
      this.$router.push('/notifications')
    },
    editCampaign(row) {
      if (this.isPendingPromotionView) {
        this.$router.push({
          path: `/promotion/${row.id}/edit`,
          query: {
            readonly: '1',
            from: 'notifications',
          },
        })
        return
      }
      this.$router.push(`/promotion/${row.id}/edit`)
    },
    openBills(row) {
      this.$router.push(`/promotion/${row.id}/bills`)
    },
    continuePay(row) {
      this.$router.push({
        path: '/promotion/checkout',
        query: {
          campaignId: row.id,
          orderId: row.latest_order_id,
        },
      })
    },
    statusLabel(status) {
      return promotionStatusLabel(status)
    },
    objectiveLabel(value) {
      return promotionObjectiveLabel(value)
    },
    budgetText(row) {
      return formatPromotionBudgetText(row)
    },
    latestBillText(row) {
      if (!row.latest_order_id) return '暂无账单'
      if (row.latest_settlement_amount === null || row.latest_settlement_amount === undefined) {
        return `账单 #${row.latest_order_id}`
      }
      return `#${row.latest_order_id} · ${(row.latest_settlement_currency || row.currency || 'USD').toUpperCase()} ${Number(row.latest_settlement_amount).toFixed(2)}`
    },
    latestBillMeta(row) {
      if (!row.latest_order_id) return '保存推广后会生成账单'
      const statusText = promotionPaymentStatusLabel(row.latest_payment_status)
      const timeText = formatDateTime(row.latest_payment_created_at || row.latest_paid_at || row.updated_at)
      return `${statusText} · ${timeText}`
    },
    canContinuePay(row) {
      return !!row.latest_order_id && isPayableStatus(row.latest_payment_status)
    },
    formatDateTime,
  },
}
</script>

<style scoped>
.promotion-list-page {
  display: grid;
  gap: 18px;
}

.promotion-list-toolbar,
.action-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.promotion-list-toolbar {
  padding: 20px 24px;
  border: 1px solid #dbe4f0;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.94);
}

.promotion-list-toolbar .el-input,
.promotion-list-toolbar .el-select {
  max-width: 280px;
}

.promotion-list-card {
  border: 1px solid #dbe4f0;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  overflow: hidden;
}

.campaign-cell,
.bill-cell {
  display: grid;
  gap: 4px;
}

.campaign-cell strong,
.bill-cell strong {
  color: #0f172a;
  font-size: 14px;
}

.campaign-cell span,
.bill-cell span {
  color: #64748b;
  font-size: 12px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-chip.is-draft {
  color: #475569;
  background: #e2e8f0;
}

.status-chip.is-active {
  color: #166534;
  background: #dcfce7;
}

.status-chip.is-paused {
  color: #9a3412;
  background: #ffedd5;
}

.status-chip.is-ended,
.status-chip.is-archived {
  color: #7c3aed;
  background: #ede9fe;
}

@media (max-width: 960px) {
  .promotion-list-toolbar {
    flex-wrap: wrap;
    padding: 16px;
  }

  .promotion-list-toolbar .el-input,
  .promotion-list-toolbar .el-select {
    max-width: none;
    width: 100%;
  }
}
</style>
